import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useDebounce } from "use-debounce";
import { customInstance } from "~/custom-instance";
import { useChatDocumentParams } from "~/helpers";
import logger from "~/utils/logger";
import { useSelectedEntityStore } from "../stores/selectedEntityStore";

const DEBOUNCE_DELAY = 250; // milliseconds
const STALE_TIME = 10 * 60 * 1000; // 10 minutes
const GC_TIME = 30 * 60 * 1000; // 30 minutes

interface SectionSummaryResponse {
  summary: string;
  truncated: boolean;
  cached: boolean;
}

/**
 * Safely extract error message from various response formats
 */
function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const data = error.response?.data;

    if (status === 404) {
      if (typeof data === "object" && data !== null) {
        const errorMsg = (data as Record<string, unknown>).error;
        const message = (data as Record<string, unknown>).message;

        if (typeof errorMsg === "string" && errorMsg) {
          if (errorMsg === "Section not found") {
            return "Section not found for this case";
          }
          if (errorMsg === "No page content available") {
            return "No summary available yet. The document may still be processing.";
          }
          return errorMsg;
        }

        if (typeof message === "string" && message) {
          return message;
        }
      }

      return "Summary not available yet. Please try again later.";
    }

    if (typeof data === "object" && data !== null) {
      const errorMsg = (data as Record<string, unknown>).error;
      const message = (data as Record<string, unknown>).message;

      if (typeof errorMsg === "string" && errorMsg) {
        return errorMsg;
      }
      if (typeof message === "string" && message) {
        return message;
      }
    }

    if (error.message) {
      return error.message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Failed to generate summary";
}

interface UseSectionSummaryResult {
  summary: string | null;
  status: "idle" | "loading" | "success" | "error";
  error: string | null;
  isLoading: boolean;
  isCached: boolean;
}

/**
 * Hook for fetching summaries (pages or sections) with optimizations:
 * - Debounces rapid changes (250ms)
 * - Only fetches when Summary tab is active (via enabled prop)
 * - Caches results with React Query (10 min stale time)
 * - Automatically cancels in-flight requests when entity changes
 * - Uses universal endpoint that handles both pages and sections
 */
export const useSectionSummary = (): UseSectionSummaryResult => {
  const { organizationId, caseId: projectId } = useChatDocumentParams();
  const selectedEntity = useSelectedEntityStore(
    (state) => state.selectedEntity
  );

  const [debouncedSelectedEntity] = useDebounce(selectedEntity, DEBOUNCE_DELAY);

  // Query for selected section from store
  const {
    data,
    isLoading,
    isError,
    error: queryError,
  } = useQuery<SectionSummaryResponse, Error>({
    queryKey: [
      "selectedSectionSummary",
      {
        orgId: organizationId,
        projectId,
        entityType: debouncedSelectedEntity?.entityType,
        entityId: debouncedSelectedEntity?.entityId,
        pageIds: debouncedSelectedEntity?.pageIds.join(",") || "",
      },
    ],
    queryFn: async ({ signal }) => {
      if (!debouncedSelectedEntity?.entityId) {
        throw new Error("Selected section entity ID is required");
      }

      const API_BASE_URL = import.meta.env.VITE_BASE_API_URL;
      if (!API_BASE_URL) {
        throw new Error(
          "VITE_BASE_API_URL environment variable is not configured"
        );
      }

      const entityType = debouncedSelectedEntity.entityType;
      const entityId = debouncedSelectedEntity.entityId;

      logger.info(
        `Fetching summary for selected entity: entityType=${entityType}, entityId=${entityId}`
      );

      try {
        const result = await customInstance<SectionSummaryResponse>({
          url: `${API_BASE_URL}/organizations/${organizationId}/projects/${projectId}/summary`,
          method: "post",
          data: {
            entityType,
            entityId,
          },
          signal,
        });

        logger.info(
          `Summary fetched for selected section ${entityId} (cached: ${result.cached}, truncated: ${result.truncated})`
        );

        return result;
      } catch (error: unknown) {
        const status = axios.isAxiosError(error)
          ? error.response?.status
          : undefined;

        logger.error(`Failed to fetch summary for ${entityType} ${entityId}`, {
          status,
          entityType,
          entityId,
        });

        throw new Error(getErrorMessage(error));
      }
    },
    enabled: !!debouncedSelectedEntity?.entityId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  let status: "idle" | "loading" | "success" | "error" = "idle";
  if (isLoading) {
    status = "loading";
  } else if (isError) {
    status = "error";
  } else if (data) {
    status = "success";
  }

  return {
    summary: data?.summary || null,
    status,
    error: isError ? queryError?.message || "Failed to generate summary" : null,
    isLoading,
    isCached: data?.cached || false,
  };
};
